'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import WorldwayNav from '@/components/WorldwayNav';

// ============================================================
// TYPES
// ============================================================

interface GADTour {
  gad_tour_id: string;
  name: string;
  slug?: string;
  description?: string;
  duration_days?: number;
  min_age?: number;
  geography?: {
    region?: { name: string };
    countries?: Array<{ name: string }>;
  };
  trip_styles?: Array<{ name: string }>;
  images?: Array<{ image_href?: string }>;
  physical_grading?: { label?: string };
  accommodation_level?: { name?: string };
  inclusions?: string[];
}

interface GADDeparture {
  gad_departure_id: string;
  start_date?: string;
  finish_date?: string;
  availability_status?: string;
  available_places?: number;
  prices?: Array<{ currency?: { iso_4217?: string }; amount?: string }>;
}

// ============================================================
// HELPERS
// ============================================================

const TRIP_STYLE_COLORS: Record<string, string> = {
  Classic: 'bg-amber-500/15 text-amber-400',
  Active: 'bg-emerald-500/15 text-emerald-400',
  Wildlife: 'bg-green-500/15 text-green-400',
  Adventure: 'bg-orange-500/15 text-orange-400',
  Cultural: 'bg-violet-500/15 text-violet-400',
  Comfort: 'bg-blue-500/15 text-blue-400',
  Premium: 'bg-rose-500/15 text-rose-400',
};

function getStyleColor(style: string): string {
  return TRIP_STYLE_COLORS[style] ?? 'bg-surface-3 text-muted-foreground';
}

function getRegionEmoji(region: string): string {
  if (region.includes('Africa')) return '🦁';
  if (region.includes('Asia')) return '🏯';
  if (region.includes('Europe')) return '🏰';
  if (region.includes('America')) return '🌎';
  if (region.includes('Pacific')) return '🌊';
  if (region.includes('Middle')) return '🕌';
  return '🌍';
}

// ============================================================
// TOUR CARD
// ============================================================

function TourCard({ tour, onBook }: { tour: GADTour; onBook: (tour: GADTour) => void }) {
  const region = tour.geography?.region?.name ?? 'Worldwide';
  const countries = tour.geography?.countries?.map((c) => c.name).join(', ') ?? '';
  const style = tour.trip_styles?.[0]?.name ?? 'Classic';
  const coverImage = tour.images?.[0]?.image_href;

  return (
    <div className="glass-card rounded-xl overflow-hidden hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer flex flex-col">
      {/* Image */}
      <div className="relative h-44 bg-gradient-to-br from-[#0D1525] to-[#1a2540] overflow-hidden">
        {coverImage ? (
          <img src={coverImage} alt={`${tour.name} tour`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-5xl opacity-40">{getRegionEmoji(region)}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050810]/80 to-transparent" />
        <div className="absolute top-3 left-3">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${getStyleColor(style)}`}>{style}</span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.15)] text-[var(--primary)] border border-[rgba(201,168,76,0.2)]">
            G Adventures
          </span>
        </div>
        {tour.duration_days && (
          <div className="absolute bottom-3 left-3 text-xs text-white/80">
            ⏱ {tour.duration_days} days
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-serif)' }}>
          {tour.name}
        </h3>
        <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-2">
          <span>{getRegionEmoji(region)} {region}</span>
          {countries && <span>· {countries.split(',')[0]}</span>}
        </div>
        {tour.description && (
          <p className="text-xs text-[var(--muted-foreground)] line-clamp-2 mb-3 flex-1">{tour.description}</p>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-[var(--border)]">
          <div className="text-xs text-[var(--muted-foreground)]">
            {tour.min_age ? `Ages ${tour.min_age}+` : 'All ages'}
          </div>
          <button
            onClick={() => onBook(tour)}
            className="text-xs font-semibold text-[var(--primary)] hover:underline"
          >
            View & Book →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// BOOKING MODAL
// ============================================================

function BookingModal({ tour, onClose }: { tour: GADTour; onClose: () => void }) {
  const router = useRouter();
  const [departures, setDepartures] = useState<GADDeparture[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDep, setSelectedDep] = useState<string>('');

  useEffect(() => {
    fetch(`/api/gadventures/tours/${tour.gad_tour_id}`)
      .then((r) => r.json())
      .then((d) => { setDepartures(d.departures ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tour.gad_tour_id]);

  const handleBook = () => {
    const dep = selectedDep || tour.gad_tour_id;
    router.push(`/booking?vertical=activity&supplier=g-adventures&product=${encodeURIComponent(tour.name)}&departure=${dep}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="p-5 border-b border-[var(--border)] flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-serif)' }}>{tour.name}</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
              {tour.geography?.region?.name} · {tour.duration_days} days · G Adventures
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] text-xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {tour.description && (
            <p className="text-sm text-[var(--muted-foreground)]">{tour.description}</p>
          )}

          {/* Inclusions */}
          {tour.inclusions && tour.inclusions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[var(--foreground)] mb-2">Inclusions</p>
              <ul className="space-y-1">
                {tour.inclusions.slice(0, 5).map((inc, i) => (
                  <li key={i} className="text-xs text-[var(--muted-foreground)] flex items-start gap-1.5">
                    <span className="text-emerald-400 mt-0.5">✓</span>{inc}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Departures */}
          <div>
            <p className="text-xs font-semibold text-[var(--foreground)] mb-2">Available Departures</p>
            {loading ? (
              <div className="text-xs text-[var(--muted-foreground)]">Loading departures...</div>
            ) : departures.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {departures.map((dep) => {
                  const price = dep.prices?.[0];
                  return (
                    <label key={dep.gad_departure_id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${selectedDep === dep.gad_departure_id ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.08)]' : 'border-[var(--border)] hover:border-[var(--primary)]/40'}`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="departure" value={dep.gad_departure_id} checked={selectedDep === dep.gad_departure_id} onChange={() => setSelectedDep(dep.gad_departure_id)} className="accent-[var(--primary)]" />
                        <div>
                          <p className="text-xs font-medium">{dep.start_date ? new Date(dep.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'}</p>
                          <p className="text-[10px] text-[var(--muted-foreground)]">
                            {dep.available_places != null ? `${dep.available_places} places left` : dep.availability_status}
                          </p>
                        </div>
                      </div>
                      {price && (
                        <span className="text-xs font-semibold text-[var(--primary)]">
                          {price.currency?.iso_4217} {parseFloat(price.amount ?? '0').toLocaleString()}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-[var(--muted-foreground)]">Contact us for departure dates and pricing.</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-[var(--border)] text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors">
              Cancel
            </button>
            <button onClick={handleBook} className="flex-1 btn-primary py-2.5 rounded-lg text-sm font-semibold">
              Book This Tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================

const TRIP_STYLES = ['All Styles', 'Classic', 'Active', 'Wildlife', 'Adventure', 'Cultural', 'Comfort', 'Premium'];
const REGIONS = ['All Regions', 'Africa & Middle East', 'Asia', 'Europe', 'South America', 'North America', 'Pacific'];

export default function GAdventuresToursPage() {
  const [tours, setTours] = useState<GADTour[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [activeStyle, setActiveStyle] = useState('All Styles');
  const [activeRegion, setActiveRegion] = useState('All Regions');
  const [selectedTour, setSelectedTour] = useState<GADTour | null>(null);
  const [configured, setConfigured] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ tours: number; departures: number } | null>(null);

  const fetchTours = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (keyword) qs.set('q', keyword);
      const res = await fetch(`/api/gadventures/tours?${qs}&limit=24`);
      const data = await res.json();
      setTours(data.tours ?? []);
      setTotal(data.total ?? 0);
      setConfigured(data.configured ?? false);
    } catch {
      setTours([]);
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  useEffect(() => {
    fetch('/api/gadventures/sync')
      .then((r) => r.json())
      .then((d) => setSyncStatus(d.catalogue))
      .catch(() => {});
  }, []);

  const filtered = tours.filter((t) => {
    const matchStyle = activeStyle === 'All Styles' || t.trip_styles?.some((s) => s.name === activeStyle);
    const matchRegion = activeRegion === 'All Regions' || t.geography?.region?.name === activeRegion;
    return matchStyle && matchRegion;
  });

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-6">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <span>/</span>
            <Link href="/tours" className="hover:text-[var(--primary)] transition-colors">Tours</Link>
            <span>/</span>
            <span className="text-[var(--foreground)]">G Adventures</span>
          </nav>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center text-xl">🌍</div>
                <div>
                  <p className="label-caps text-[var(--primary)] text-xs">Powered by G Adventures</p>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${configured ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${configured ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      {configured ? 'Live' : 'Awaiting Credentials'}
                    </span>
                  </div>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
                G Adventures Tours<br />
                <span className="gold-text-gradient">Adventure Awaits.</span>
              </h1>
              <p className="text-base text-[var(--muted-foreground)] max-w-xl">
                Small group adventures, cultural immersions, and wildlife expeditions across 100+ countries. Authentic travel with expert local guides.
              </p>
            </div>

            {syncStatus && (
              <div className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.15)] min-w-[180px]">
                <p className="text-xs text-[var(--muted-foreground)] mb-3 font-medium">Catalogue Status</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--muted-foreground)]">Tours</span>
                    <span className="font-semibold text-[var(--primary)]">{syncStatus.tours.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[var(--muted-foreground)]">Departures</span>
                    <span className="font-semibold text-[var(--foreground)]">{syncStatus.departures.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="glass-card rounded-2xl p-4 border border-[rgba(201,168,76,0.15)] mb-5">
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                placeholder="Search G Adventures tours, destinations, styles..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                className="flex-1 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
              />
              <button onClick={fetchTours} className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap">
                Search Tours
              </button>
            </div>
          </div>

          {/* Style filters */}
          <div className="flex flex-wrap gap-2 mb-2">
            {TRIP_STYLES.map((s) => (
              <button key={s} onClick={() => setActiveStyle(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeStyle === s ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'}`}>{s}</button>
            ))}
          </div>
          {/* Region filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {REGIONS.map((r) => (
              <button key={r} onClick={() => setActiveRegion(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeRegion === r ? 'bg-[var(--surface-3)] text-[var(--foreground)] border border-[var(--primary)]/50' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'}`}>{r}</button>
            ))}
          </div>

          <p className="text-xs text-[var(--muted-foreground)] mb-4">
            {loading ? 'Loading tours...' : `${filtered.length} tour${filtered.length !== 1 ? 's' : ''} found`}
            {total > filtered.length && ` (${total} total in catalogue)`}
          </p>

          {/* Tour Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="glass-card rounded-xl h-72 animate-pulse" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((tour) => (
                <TourCard key={tour.gad_tour_id} tour={tour} onBook={setSelectedTour} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🌍</div>
              <p className="text-[var(--muted-foreground)] text-sm mb-4">No tours found matching your criteria.</p>
              {!configured && (
                <p className="text-xs text-amber-400">G Adventures credentials not yet configured. Add credentials in the Enterprise API Credentials Manager to enable live tour data.</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'AI Trip Builder', desc: 'Let our AI Concierge build your perfect G Adventures itinerary.', href: '/ai-concierge', icon: '🤖' },
              { title: 'Tailor-Made Journeys', desc: 'Combine G Adventures tours with flights, hotels, and transfers.', href: '/dynamic-packages', icon: '✨' },
              { title: 'All Tours', desc: 'Browse our complete curated tours collection.', href: '/tours', icon: '🗺️' },
            ].map((item) => (
              <Link key={item.title} href={item.href} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-sm font-bold mb-2 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{item.title}</h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · G Adventures Partner</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/tours" className="hover:text-[var(--primary)] transition-colors">All Tours</Link>
            <Link href="/small-group" className="hover:text-[var(--primary)] transition-colors">Small Group</Link>
            <Link href="/ai-concierge" className="hover:text-[var(--primary)] transition-colors">AI Concierge</Link>
          </div>
        </div>
      </footer>

      {selectedTour && <BookingModal tour={selectedTour} onClose={() => setSelectedTour(null)} />}
    </div>
  );
}
